import {
  useEsrsAssessmentMaterialStandardsQuery,
  useGetEsrsCategoriesQuery,
  useGetParentMaterialityAssessmentQuery,
} from 'models';
import { useMemo } from 'react';
import {
  EsrsCategoryMap,
  StandardsOverviewData,
} from '../../GroupAssessment/GroupAssessmentOverview.hooks';
import { MetricDataCollection } from '../../MaterialityAssessment';
import { isMetricStateMaterial, mfStandardRefs, orderCategories } from 'containers/Esrs/utils';
import { captureException } from '@sentry/react';
import { DataCollectionLevel } from '../../DataCollection';
import { uniq } from 'lodash';
import { drProgressConditions, getRelevantDR } from '../..';
import { useParams } from 'react-router-dom';

export const useGetSubsidiaryStandardsOverview = (filter: string, esrsAssessmentId?: string) => {
  const { framework = '' } = useParams();
  const { data, loading: loadingAssessment } = useEsrsAssessmentMaterialStandardsQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const { data: parentMaterialityAssessmentData, loading: loadingParentMaterialityAssessment } =
    useGetParentMaterialityAssessmentQuery({
      variables: { childAssessmentId: esrsAssessmentId },
      skip: !esrsAssessmentId,
    });

  const { data: esrsCategoriesData, loading: loadingCategories } = useGetEsrsCategoriesQuery({
    variables: { frameworkRef: framework },
    skip: !framework,
  });

  const categories = useMemo(() => {
    return orderCategories(esrsCategoriesData?.EsrsCategory ?? []);
  }, [esrsCategoriesData]);

  const groupMaterialityMap: EsrsCategoryMap[] = useMemo(() => {
    const materialStandards = data?.esrsAssessment?.materialStandards;
    const parentMaterialityAssessments =
      parentMaterialityAssessmentData?.EsrsAssessment_by_pk?.parentAssessment?.materialStandards;

    if (materialStandards) {
      const generalStandardSubsidiaryDataCollection = parentMaterialityAssessments
        ?.filter((standard) => standard.standard.categoryRef === 'general')
        .filter((standard) =>
          standard.materialMetrics.some(
            (metric) => metric.dataCollection === DataCollectionLevel.subsidiaries
          )
        );
      const generalStandardSubsidiaryCollectOnly = materialStandards
        ?.filter((standard) => standard.standard.category.reference === 'general')
        .filter((standard) =>
          standard.materialMetrics.some((metric) => metric.isMaterial === true)
        );
      const filteredCategories = categories.filter(
        (category) =>
          !(category.reference === 'general') ||
          !data?.esrsAssessment?.collectOnly ||
          generalStandardSubsidiaryDataCollection?.length ||
          generalStandardSubsidiaryCollectOnly.length
      );

      return filteredCategories.map((category) => {
        return {
          categoryName: category.title,
          categoryRef: category.reference,
          materialStandards:
            category.standards
              ?.filter((standard) => {
                const parentStandard = parentMaterialityAssessments?.find(
                  (materialityAssessment) =>
                    materialityAssessment.standardRef === standard.reference
                );
                const materialStandard = materialStandards.find((materialStandard) => {
                  return materialStandard.standardRef === standard.reference;
                });
                return (
                  (parentStandard?.materialMetrics &&
                    parentStandard?.materialMetrics?.some((mm) => mm.isMaterial)) ||
                  (materialStandard?.materialMetrics &&
                    materialStandard?.materialMetrics.some((mm) => mm.isMaterial))
                );
              })
              .map((standard) => {
                const isMFStandard = mfStandardRefs.includes(standard.reference);
                const parentStandard = parentMaterialityAssessments?.find(
                  (materialityAssessment) =>
                    materialityAssessment.standardRef === standard.reference
                );
                const materialStandard = materialStandards.find((materialStandard) => {
                  return materialStandard.standardRef === standard.reference;
                });

                const standardApDrs =
                  (isMFStandard
                    ? standard.specificMFStandard
                    : standard
                  )?.disclosureRequirementGroups
                    .flatMap((drg) => drg.requirements.flatMap((dr) => dr.reference))
                    ?.filter((s): s is string => !!s) ?? [];

                const parentRelevantDRs =
                  parentStandard?.materialMetrics
                    .filter(
                      (mm) =>
                        drProgressConditions(mm, standardApDrs, framework) &&
                        mm.dataCollection === DataCollectionLevel.subsidiaries &&
                        (mm.metric.childrenMetricsCount === 0 ||
                          parentStandard?.materialMetrics.some(
                            (metric) =>
                              metric.dataCollection === DataCollectionLevel.subsidiaries &&
                              mm.metric.childrenMetrics?.some(
                                (cm) => cm.childMetricRef === metric.metricRef
                              )
                          ))
                    )
                    ?.map((mm) => getRelevantDR(mm.metric, framework)) ?? [];

                const companyRelevantDRs =
                  materialStandard?.materialMetrics
                    .filter((mm) => drProgressConditions(mm, standardApDrs, framework))
                    ?.map((mm) => getRelevantDR(mm.metric, framework)) ?? [];

                const drRefs = uniq([...parentRelevantDRs, ...companyRelevantDRs]);

                return {
                  standardRef: standard.reference,
                  standardName: standard.title,
                  description: standard.description,
                  parentEsrsAssessmentId:
                    parentMaterialityAssessmentData?.EsrsAssessment_by_pk?.parentAssessment?.id,
                  drRefs: drRefs,
                  isMaterialForGroup: parentStandard?.isMaterial === true,
                  isMaterialForSubsidiary: materialStandard?.isMaterial === true,
                  isMFStandard: isMFStandard,
                  frameworkRef: materialStandard?.standard.frameworkRef ?? '',
                };
              })
              .filter(
                (standard) =>
                  standard.isMaterialForGroup == true || standard.isMaterialForSubsidiary == true
              ) ?? [],
        };
      });
    } else return [];
  }, [data, parentMaterialityAssessmentData, esrsCategoriesData, filter]);

  const loading = useMemo(() => {
    return loadingAssessment || loadingCategories || loadingParentMaterialityAssessment;
  }, [loadingAssessment, loadingCategories, loadingParentMaterialityAssessment]);

  const groupStandardsOverviewData: StandardsOverviewData = {
    categories: groupMaterialityMap,
    loading,
  };

  return groupStandardsOverviewData;
};

export const useGetBusinessUnitStandardsOverview = (
  filter: string,
  esrsAssessmentId?: string,
  isBusinessUnitLevel?: boolean
) => {
  const { framework = '' } = useParams();
  const { data, loading: loadingAssessment } = useEsrsAssessmentMaterialStandardsQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const { data: parentMaterialityAssessmentData, loading: loadingParentMaterialityAssessment } =
    useGetParentMaterialityAssessmentQuery({
      variables: { childAssessmentId: esrsAssessmentId },
      skip: !esrsAssessmentId,
    });

  const { data: esrsCategoriesData, loading: loadingCategories } = useGetEsrsCategoriesQuery({
    variables: { frameworkRef: framework },
    skip: !framework,
  });

  const categories = useMemo(() => {
    return orderCategories(esrsCategoriesData?.EsrsCategory ?? []);
  }, [esrsCategoriesData]);

  const materialityMap: EsrsCategoryMap[] = useMemo(() => {
    try {
      const materialStandardsViaMetrics = data?.esrsAssessment?.materialStandards?.map(
        (materialStandard) => {
          return {
            standardRef: materialStandard.standardRef,
            dataCollection: materialStandard.materialMetrics?.map((materialMetric) => {
              return materialMetric.dataCollection;
            }),
          };
        }
      );

      const materialStandards = data?.esrsAssessment?.materialStandards;
      const parentMaterialityAssessments =
        parentMaterialityAssessmentData?.EsrsAssessment_by_pk?.parentAssessment?.materialStandards;

      if (materialStandards) {
        return categories.map((category) => {
          return {
            categoryName: category.title,
            categoryRef: category.reference,
            materialStandards:
              category.standards
                ?.map((standard) => {
                  const isMFStandard = mfStandardRefs.includes(standard.reference);
                  const parentStandard = parentMaterialityAssessments?.find(
                    (ms) => ms.standardRef === standard.reference
                  );
                  const materialStandard = materialStandards?.find(
                    (ms) => ms.standardRef === standard.reference
                  );
                  const standardApDrs =
                    (isMFStandard
                      ? standard.specificMFStandard
                      : standard
                    )?.disclosureRequirementGroups
                      .flatMap((drg) => drg.requirements.flatMap((dr) => dr.reference))
                      ?.filter((s): s is string => !!s) ?? [];

                  const parentRelevantDRs =
                    parentStandard?.materialMetrics
                      .filter(
                        (mm) =>
                          drProgressConditions(mm, standardApDrs, framework) &&
                          mm.dataCollection === DataCollectionLevel.subsidiaries &&
                          (mm.metric?.childrenMetricsCount ?? 0) === 0 &&
                          materialStandard?.materialMetrics.find(
                            (cm) =>
                              cm.metricRef === mm.metricRef &&
                              cm.dataCollection === DataCollectionLevel.reportingUnits
                          )
                      )
                      .map((m) => m.metric.disclosureRequirementRef) ?? [];

                  const reportingUnitMetrics = materialStandard?.materialMetrics.filter(
                    (mm) => mm.dataCollection === DataCollectionLevel.reportingUnits
                  );

                  const companyParentMetrics = materialStandard?.materialMetrics
                    .filter(
                      (mm) =>
                        mm.dataCollection === DataCollectionLevel.company &&
                        mm.metric.childrenMetrics?.some((child) =>
                          reportingUnitMetrics?.some(
                            (metric) => metric.metricRef === child.childMetricRef
                          )
                        )
                    )
                    .map((met) => {
                      const parentMetric = parentStandard?.materialMetrics.find(
                        (mm) =>
                          mm.dataCollection === DataCollectionLevel.subsidiaries &&
                          mm.metricRef === met.metricRef
                      );
                      return { ...met, isMaterial: parentMetric?.isMaterial || met.isMaterial };
                    });

                  reportingUnitMetrics?.push(...(companyParentMetrics ?? []));

                  const companyRelevantDRs =
                    reportingUnitMetrics
                      ?.filter((mm) => drProgressConditions(mm, standardApDrs, framework))
                      .map((m) => m.metric.disclosureRequirementRef) ?? [];

                  const drRefs = uniq([...parentRelevantDRs, ...companyRelevantDRs]);

                  return {
                    standardRef: standard.reference,
                    standardName: standard.title,
                    description: standard.description,
                    isMaterialForGroup: parentStandard?.isMaterial,
                    isMaterialForSubsidiary: isMetricStateMaterial(materialStandard?.isMaterial),
                    materialMetrics: materialStandard?.materialMetrics,
                    parentMaterialMetrics:
                      parentStandard?.materialMetrics.filter((metric) => metric.isMaterial) ?? [],
                    parentEsrsAssessmentId:
                      parentMaterialityAssessmentData?.EsrsAssessment_by_pk?.parentAssessment?.id,
                    drRefs: drRefs,
                    isMFStandard,
                  };
                })
                .filter((standard) => {
                  const metricsDataCollection = materialStandardsViaMetrics?.find(
                    (materialStandardViaMetric) =>
                      materialStandardViaMetric.standardRef === standard.standardRef
                  )?.dataCollection;

                  const dataCollectionCondition = isBusinessUnitLevel
                    ? metricsDataCollection?.some(
                        (dataCollection) =>
                          dataCollection === MetricDataCollection.reportingUnitLevel
                      )
                    : metricsDataCollection?.some(
                        (dataCollection) => dataCollection === MetricDataCollection.companyLevel
                      );

                  if (filter === 'all') {
                    return (
                      (standard.isMaterialForGroup == true ||
                        standard.isMaterialForSubsidiary == true) &&
                      dataCollectionCondition
                    );
                  } else if (filter === 'not-material') {
                    return (
                      standard.isMaterialForGroup == true &&
                      standard.isMaterialForSubsidiary == false &&
                      dataCollectionCondition
                    );
                  } else if (filter === 'material') {
                    return standard.isMaterialForSubsidiary == true && dataCollectionCondition;
                  }
                }) ?? [],
          };
        }) as unknown as EsrsCategoryMap[];
      } else return [];
    } catch (e) {
      captureException(Error('Failed to get business unit standards overview'), { extra: { e } });
      return [];
    }
  }, [data, parentMaterialityAssessmentData, esrsCategoriesData, filter, isBusinessUnitLevel]);

  const loading = useMemo(() => {
    return loadingAssessment || loadingCategories || loadingParentMaterialityAssessment;
  }, [loadingAssessment, loadingCategories, loadingParentMaterialityAssessment]);

  const standardsOverviewData: StandardsOverviewData = {
    categories: materialityMap,
    loading,
  };

  return standardsOverviewData;
};
